import { useSearchParams } from "react-router-dom";
import { ActionBanner } from "@/components/ActionBanner";
import { useEffect, useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, download, errMsg, fmtDate, fmtMoney, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { PageHeader } from "@/components/common";
import { useDebounced } from "@/components/DataTable";

const ACTIONS = { supplier: "Assign supplier", recipe: "Add recipe", discontinued: "Mark discontinued", out_of_scope: "Mark out of scope" };

const ActionDialog = ({ act, row, onClose, onDone }) => {
  const [v, setV] = useState({ vendor_id: "", rm: "", ratio: "1", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const go = async () => {
    setBusy(true);
    const m = encodeURIComponent(row.material);
    try {
      if (act === "supplier") await api.post(`/untraceable/${m}/supplier`, { vendor_id: v.vendor_id });
      else if (act === "recipe") await api.post(`/untraceable/${m}/recipe`, { inputs: [{ material: v.rm, ratio: Number(v.ratio) }] });
      else await api.post(`/untraceable/${m}/disposition`, { status: act, reason: v.reason });
      toast.success(`${row.material}: ${ACTIONS[act].toLowerCase()} — removed from the worklist`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="untraceable-action-dialog">
        <DialogHeader><DialogTitle>{ACTIONS[act]} · {row.material}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{row.description}</p>
        {act === "supplier" && <Input data-testid="untraceable-vendor-input" placeholder="SAP vendor number" value={v.vendor_id} onChange={set("vendor_id")} />}
        {act === "recipe" && <div className="flex gap-2"><Input data-testid="untraceable-rm-input" placeholder="Raw material (in-scope)" value={v.rm} onChange={set("rm")} />
          <Input data-testid="untraceable-ratio-input" className="w-32" placeholder="kg per unit" value={v.ratio} onChange={set("ratio")} /></div>}
        {(act === "discontinued" || act === "out_of_scope") && <Input data-testid="untraceable-reason-input" placeholder="Reason (kept in the audit log)" value={v.reason} onChange={set("reason")} />}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="untraceable-action-confirm" disabled={busy} onClick={go}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{ACTIONS[act]}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Untraceable() {
  const { can, user } = useAuth();
  const { params } = usePlant();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(true);
  const [sp] = useSearchParams();
  const action = sp.get("action") || "";
  const [d, setD] = useState(null);
  const [act, setAct] = useState(null);
  const [reload, setReload] = useState(0);
  const dq = useDebounced(q);
  const comp = ["admin", "compliance"].includes(user?.role);
  useEffect(() => { setD(null); api.get("/untraceable", { params: { ...params, q: dq || undefined, active, action: action || undefined, stock: action ? sp.get("stock") || "in" : undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [dq, active, reload, params, action, sp]);
  return (
    <div data-testid="untraceable-page">
      <PageHeader title="Untraceable products" subtitle="In-scope products with no recipe (raw materials) and no PO line. Fix these first: assign the supplier, add the recipe, or mark them discontinued / out of scope. Uploading older PO extracts clears them automatically.">
        <Button variant="outline" data-testid="untraceable-export-btn" onClick={() => download(`/untraceable/export?active=${active}`, "untraceable_products.xlsx")}><Download className="mr-1.5 h-4 w-4" />
      <ActionBanner action={action} total={d?.total} clearTo="/untraceable" />Excel</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="untraceable-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search material or description…" className="h-9 bg-white pl-9" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={active} onCheckedChange={setActive} data-testid="untraceable-active-toggle" />Only with stock or sales</label>
        {d && <span className="text-sm text-slate-600" data-testid="untraceable-total"><b>{fmtNum(d.total)}</b> products · <b>{fmtNum(d.eu_lines)}</b> NI/IE/EU sales lines · {fmtMoney(d.stock_value_gbp)} stock</span>}
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="untraceable-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>
              {["Material", "Description", "Plants", "HS code", "Stock qty", "Stock £", "NI/IE/EU lines", "Last sale", "Suggested cause", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{d.items.map((r) => (
              <tr key={r.material} className="border-t border-slate-100" data-testid={`untraceable-row-${r.material}`}>
                <td className="px-3 py-2 font-mono text-[13px]">{r.material}</td>
                <td className="max-w-[260px] truncate px-3 py-2" title={r.description}>{r.description}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.plants.join(", ")}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.commodity_code || "—"}</td>
                <td className="px-3 py-2 text-right">{fmtNum(r.stock_qty)}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r.stock_value_gbp)}</td>
                <td className="px-3 py-2 text-right font-semibold" data-testid={`untraceable-eu-${r.material}`}>{fmtNum(r.eu_lines)}</td>
                <td className="px-3 py-2">{fmtDate(r.last_sale)}</td>
                <td className="px-3 py-2 text-xs text-slate-600" data-testid={`untraceable-cause-${r.material}`}>{r.cause}</td>
                <td className="px-3 py-2"><select data-testid={`untraceable-action-${r.material}`} value="" onChange={(e) => setAct({ a: e.target.value, r })} disabled={!can.upload} className="h-8 rounded border border-slate-200 bg-white px-1 text-xs">
                  <option value="">Fix…</option><option value="supplier">Assign supplier</option><option value="recipe">Add recipe</option>
                  {comp && <><option value="discontinued">Mark discontinued</option><option value="out_of_scope">Mark out of scope</option></>}</select></td>
              </tr>))}</tbody>
          </table>
          {d.total > d.items.length && <p className="px-3 py-2 text-xs text-slate-500">Showing the top {d.items.length} by priority — download Excel for all {fmtNum(d.total)}.</p>}
        </div>)}
      {act && <ActionDialog act={act.a} row={act.r} onClose={() => setAct(null)} onDone={() => { setAct(null); setReload((x) => x + 1); }} />}
    </div>
  );
}
